import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';

import type { Client } from '@/clients/types';
import type { FeatureOverlayState } from '@/components/map/utils/mapLayers';
import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import {
    VAN_MARKER_ASSET_PATH,
    VAN_MARKER_IMAGE_ID,
    VAN_MARKER_PIXEL_RATIO,
} from '@/components/map/utils/vanMarkerArt';
import { fetchVehiclesMapData } from '@/api/vehiclesApi';
import { createHaloPulse } from '@/utils/haloPulse';
import type { HaloPulse } from '@/utils/haloPulse';
import { attachIntervalLoader, EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';

export const TECHNICIAN_VANS_SOURCE_ID = 'orbit-technician-vans-source';

/**
 * Ingestion refreshes positions every 60s; polling at half that keeps worst-case display
 * latency ~90s instead of ~120s for a payload of tens of features.
 */
const VANS_POLL_INTERVAL_MS = 30_000;

// Selected-van halo: the POI selection halo's behaviour (same opacities and radar pulse
// timings, so selection feels identical across marker families) tinted Origin Blue —
// the van marker encodes no status, so the selection ring takes the brand colour.
// Radii are the designer's street-level reference (inner ring ~47px, outer ~81px around
// the ~94px van) at the ramp's full scale; they ride the same zoom ramp as the marker.
// Layer paints cannot reference CSS variables; the token is hardcoded like every layer.
const VAN_HALO_COLOR = '#007CFF';
const VAN_HALO_LAYER_IDS = [ORBIT_LAYER_IDS.technicianVanHaloOuter, ORBIT_LAYER_IDS.technicianVanHaloInner] as const;
const HALO_INNER_RADIUS = 46;
const HALO_OUTER_RADIUS = 80;
const HALO_INNER_OPACITY = 0.35;
const HALO_OUTER_OPACITY = 0.2;
const HALO_PULSE_MIN_RADIUS = 2;
const HALO_PULSE_MAX_RADIUS = 84;
const HALO_PULSE_PEAK_OPACITY = 0.35;
const HALO_PULSE_CYCLE_MS = 1100;
const HALO_PULSE_OVERLAP_MS = 300;
const HALO_PULSE_PULSES = 3;
const HALO_PULSE_SETTLE_MS = 350;

/** The id filter shared by both halo layers: the selected van, or nothing. */
function vanHaloFilter(selectedVanId: string | null): maplibregl.FilterSpecification {
    return ['==', ['get', 'id'], selectedVanId ?? ''];
}

// The van's zoom ramp: map-rotated, the van reads as an object on the road — a fixed
// screen size would appear to SHRINK against the growing streetscape as the user zooms
// in. Full design size (VAN_MARKER_SIZE, the street-level reference) from z18, a touch
// smaller at the typical tracking zoom, receding on wide views. (True ground scale is not
// the aim — a real van would be 2px at z15.) The halo radii ride the same ramp so the
// ring always clears the marker.
const VAN_ZOOM_SCALE_STOPS = [
    [11, 0.5],
    [14, 0.85],
    [18, 1],
] as const;

/** `value`, scaled by the van zoom ramp — for icon-size (value 1) and halo radii alike. */
function vanZoomScaled(value: number): maplibregl.ExpressionSpecification {
    const expression: unknown[] = [
        'interpolate',
        ['linear'],
        ['zoom'],
        ...VAN_ZOOM_SCALE_STOPS.flatMap(([zoom, scale]) => [zoom, value * scale]),
    ];
    return expression as maplibregl.ExpressionSpecification;
}

/** Loads the single van sprite and registers it, guarded against style.load races. */
export async function registerVanImages(mlMap: maplibregl.Map): Promise<void> {
    if (mlMap.hasImage(VAN_MARKER_IMAGE_ID)) return;
    try {
        const { data } = await mlMap.loadImage(VAN_MARKER_ASSET_PATH);
        if (!mlMap.hasImage(VAN_MARKER_IMAGE_ID)) {
            mlMap.addImage(VAN_MARKER_IMAGE_ID, data, { pixelRatio: VAN_MARKER_PIXEL_RATIO });
        }
    } catch {
        // Progressive enhancement — a missing marker image falls back to the default icon.
    }
}

function registerVansSourceAndLayers(mlMap: maplibregl.Map, data: FeatureCollection, visible: boolean): void {
    const visibility = visible ? 'visible' : 'none';

    try {
        if (mlMap.getSource(TECHNICIAN_VANS_SOURCE_ID)) {
            (mlMap.getSource(TECHNICIAN_VANS_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            mlMap.addSource(TECHNICIAN_VANS_SOURCE_ID, { type: 'geojson', data, promoteId: 'id' });
        }
    } catch {
        return;
    }

    // Halo circle layers register before the van symbol layer so the marker renders on
    // top of its ring. Idle until a van is selected: empty id filter, zero radius, and
    // instant transitions so the pulse animation owns every radius/opacity change.
    for (const [layerId, opacity] of [
        [ORBIT_LAYER_IDS.technicianVanHaloOuter, HALO_OUTER_OPACITY],
        [ORBIT_LAYER_IDS.technicianVanHaloInner, HALO_INNER_OPACITY],
    ] as const) {
        if (!mlMap.getLayer(layerId)) {
            try {
                addOrbitLayer(
                    mlMap,
                    {
                        id: layerId,
                        type: 'circle',
                        source: TECHNICIAN_VANS_SOURCE_ID,
                        filter: vanHaloFilter(null),
                        layout: { visibility },
                        paint: {
                            'circle-radius': 0,
                            'circle-radius-transition': { duration: 0 },
                            'circle-opacity-transition': { duration: 0 },
                            'circle-color': VAN_HALO_COLOR,
                            'circle-opacity': opacity,
                        },
                    },
                    'assetOverlays',
                );
            } catch {
                // Progressive enhancement — layer failures must not break the map.
            }
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.technicianVans)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.technicianVans,
                    type: 'symbol',
                    source: TECHNICIAN_VANS_SOURCE_ID,
                    // No minzoom and no semantic-zoom band, deliberately: a live van should be
                    // visible whenever the user has the vans toggle on, at any scale.
                    layout: {
                        visibility,
                        'icon-image': VAN_MARKER_IMAGE_ID,
                        // Centre-anchored, unlike the bottom-anchored teardrop pins: the icon
                        // rotates with the vehicle heading, and rotating a bottom-anchored
                        // marker swings it around its tip instead of turning in place.
                        'icon-anchor': 'center',
                        'icon-rotate': ['coalesce', ['get', 'heading'], 0],
                        'icon-rotation-alignment': 'map',
                        // The shared zoom ramp — see VAN_ZOOM_SCALE_STOPS for the why.
                        'icon-size': vanZoomScaled(1),
                        // The fleet is tens of vans; every one must render, always.
                        'icon-allow-overlap': true,
                    },
                },
                'assetOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }
}

/**
 * Live technician-van positions: polls GET /vehicles/md every 30s while enabled and the
 * vans overlay is on. `enabled` is the per-user capability — when it is false (or revoked
 * mid-session via the poller's 403 → `onForbidden`), no vehicle request is ever made and
 * the source is cleared, so no stale van lingers after access is removed.
 */
export function useTechnicianVansLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    selectedClient: Client | null,
    featureOverlays: FeatureOverlayState,
    enabled: boolean,
    onForbidden: () => void,
    /** The van whose marker carries the Origin Blue selection halo, or null for none. */
    selectedVanId: string | null = null,
): void {
    const vansDataRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);

    // Refs let the registration/style-reload handler and the poller's forbidden callback
    // read current values without re-triggering their effects.
    const overlaysRef = useRef(featureOverlays);
    const onForbiddenRef = useRef(onForbidden);
    const selectedVanIdRef = useRef(selectedVanId);
    useLayoutEffect(() => {
        overlaysRef.current = featureOverlays;
        onForbiddenRef.current = onForbidden;
        selectedVanIdRef.current = selectedVanId;
    });

    const haloPulseRef = useRef<HaloPulse | null>(null);
    const haloPlayedForRef = useRef<string | null>(null);

    // Poll while the capability holds and the overlay is on. The reset on every re-run is
    // what clears cached vans the moment either gate goes false.
    const loadData = enabled && featureOverlays.technicianVans;
    useEffect(() => {
        vansDataRef.current = EMPTY_FEATURE_COLLECTION;
        const mlMap = mapRef.current?.getMap();
        if (mlMap) setGeoJsonSourceData(mlMap, TECHNICIAN_VANS_SOURCE_ID, EMPTY_FEATURE_COLLECTION);

        if (!loadData || !selectedClient || !isMapLoaded) return;

        return attachIntervalLoader(mapRef, {
            sourceId: TECHNICIAN_VANS_SOURCE_ID,
            intervalMs: VANS_POLL_INTERVAL_MS,
            dataRef: vansDataRef,
            fetchFn: fetchVehiclesMapData,
            onForbidden: () => {
                onForbiddenRef.current();
            },
        });
    }, [selectedClient, isMapLoaded, loadData, mapRef]);

    // Register source, images and layer when ready, and re-register on every style.load
    // (base map switches reload all sources and layers). Reuses already-loaded data.
    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const register = () => {
            registerVansSourceAndLayers(mlMap, vansDataRef.current, overlaysRef.current.technicianVans);
            void registerVanImages(mlMap).catch(() => {
                // Progressive enhancement — missing images fall back to the default icon.
            });
            // A style reload rebuilds the halo layers idle — restore the current selection
            // at the settled halo state (the pulse only plays on a fresh selection).
            const id = selectedVanIdRef.current;
            for (const layerId of VAN_HALO_LAYER_IDS) {
                if (mlMap.getLayer(layerId)) mlMap.setFilter(layerId, vanHaloFilter(id));
            }
            if (id) {
                if (mlMap.getLayer(ORBIT_LAYER_IDS.technicianVanHaloInner)) {
                    mlMap.setPaintProperty(
                        ORBIT_LAYER_IDS.technicianVanHaloInner,
                        'circle-radius',
                        vanZoomScaled(HALO_INNER_RADIUS),
                    );
                }
                if (mlMap.getLayer(ORBIT_LAYER_IDS.technicianVanHaloOuter)) {
                    mlMap.setPaintProperty(
                        ORBIT_LAYER_IDS.technicianVanHaloOuter,
                        'circle-radius',
                        vanZoomScaled(HALO_OUTER_RADIUS),
                    );
                }
            }
        };

        register();
        mlMap.on('style.load', register);

        return () => {
            mlMap.off('style.load', register);
        };
    }, [enabled, isMapLoaded, mapRef]);

    // Selection halo: id filter plus the radar ripple, matching the POI selection halo.
    // No cleanup stop — StrictMode would restart it mid-flight; the pulse self-terminates
    // and its paint writes are guarded.
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        for (const layerId of VAN_HALO_LAYER_IDS) {
            if (mlMap.getLayer(layerId)) mlMap.setFilter(layerId, vanHaloFilter(selectedVanId));
        }

        if (selectedVanId == null) {
            haloPlayedForRef.current = null;
            return;
        }
        if (selectedVanId === haloPlayedForRef.current) return;
        haloPlayedForRef.current = selectedVanId;

        haloPulseRef.current?.stop();
        haloPulseRef.current = null;

        const reducedMotion =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reducedMotion) {
            if (mlMap.getLayer(ORBIT_LAYER_IDS.technicianVanHaloInner)) {
                mlMap.setPaintProperty(
                    ORBIT_LAYER_IDS.technicianVanHaloInner,
                    'circle-radius',
                    vanZoomScaled(HALO_INNER_RADIUS),
                );
            }
            if (mlMap.getLayer(ORBIT_LAYER_IDS.technicianVanHaloOuter)) {
                mlMap.setPaintProperty(
                    ORBIT_LAYER_IDS.technicianVanHaloOuter,
                    'circle-radius',
                    vanZoomScaled(HALO_OUTER_RADIUS),
                );
            }
            return;
        }

        haloPulseRef.current = createHaloPulse(mlMap, {
            innerLayerId: ORBIT_LAYER_IDS.technicianVanHaloInner,
            outerLayerId: ORBIT_LAYER_IDS.technicianVanHaloOuter,
            minRadius: HALO_PULSE_MIN_RADIUS,
            maxRadius: HALO_PULSE_MAX_RADIUS,
            peakOpacity: HALO_PULSE_PEAK_OPACITY,
            cycleMs: HALO_PULSE_CYCLE_MS,
            overlapMs: HALO_PULSE_OVERLAP_MS,
            pulses: HALO_PULSE_PULSES,
            settleMs: HALO_PULSE_SETTLE_MS,
            innerSettleRadius: HALO_INNER_RADIUS,
            innerSettleOpacity: HALO_INNER_OPACITY,
            outerSettleRadius: HALO_OUTER_RADIUS,
            outerSettleOpacity: HALO_OUTER_OPACITY,
            // Every frame's radius rides the van zoom ramp, so the ripple and the settled
            // halos stay sized to the zoom-scaled marker.
            toRadiusPaintValue: vanZoomScaled,
        });
        haloPulseRef.current.play();
    }, [isMapLoaded, selectedVanId, mapRef]);
}
